/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    CheckCircle2,
    Network,
    SlidersHorizontal,
    Tags,
} from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

/* Import configuration */
import type { TimeSeriesGroupKey } from '@/config/eeCheckConfig';
import { HAS_NEUTRAUBLING_SENSOR_DATA } from '@/config/csvSensors';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import type { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import type { SensorGroupFilter } from '@/lib/sensors/demo/demoDeviceSensors';
import {
    getDemoSensorDeviceOptionGroups,
    type DemoSensorDeviceAvailability,
    type DemoSensorDeviceGroupBy,
} from '@/lib/sensors/demo/demoSensorDeviceSelection';

interface DemoSensorDeviceFilterControlsProps {
    availability: DemoSensorDeviceAvailability;
    customer: TimeSeriesGroupKey;
    deviceSensorCatalog: DeviceSensorCatalog;
    extraControl?: ReactNode;
    groupBy: DemoSensorDeviceGroupBy;
    groupFilter: SensorGroupFilter;
    onAvailabilityChange: (availability: DemoSensorDeviceAvailability) => void;
    onCustomerChange: (customer: TimeSeriesGroupKey) => void;
    onDeviceChange: (device: string) => void;
    onGroupByChange: (groupBy: DemoSensorDeviceGroupBy) => void;
    onGroupFilterChange: (groupFilter: SensorGroupFilter) => void;
    selectedDevice: string;
    virtualDevices: string[];
}

function DemoSensorDeviceFilterControls({
    availability,
    customer,
    deviceSensorCatalog,
    extraControl,
    groupBy,
    groupFilter,
    onAvailabilityChange,
    onCustomerChange,
    onDeviceChange,
    onGroupByChange,
    onGroupFilterChange,
    selectedDevice,
    virtualDevices,
}: DemoSensorDeviceFilterControlsProps) {
    const { t } = useI18n();
    const virtualDeviceGroups = useMemo(() => {
        return getDemoSensorDeviceOptionGroups(deviceSensorCatalog, virtualDevices, groupBy, t);
    }, [deviceSensorCatalog, groupBy, t, virtualDevices]);

    return (
        <section className="dashboard-panel flex items-stretch justify-start p-5">
            <div className={`grid w-full grid-cols-1 gap-y-4 text-left xl:divide-x xl:divide-base-content ${extraControl ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
                <div className="flex min-h-12 items-center gap-3 xl:pr-5">
                    <label className="text-sm font-semibold" htmlFor="demo-sensor-customer-select">
                        {t('Customer')}
                    </label>
                    <select
                        id="demo-sensor-customer-select"
                        className="select select-bordered w-auto"
                        value={customer}
                        onChange={(event) => onCustomerChange(event.target.value as TimeSeriesGroupKey)}
                    >
                        {HAS_NEUTRAUBLING_SENSOR_DATA ? (
                            <option value="neutraubling">{t('Neutraubling')}</option>
                        ) : null}
                        <option value="muenster">{t('Münster')}</option>
                    </select>
                </div>

                <div className="flex min-h-12 items-center gap-3 xl:px-5">
                    <span className="text-sm font-semibold">{t('View')}</span>
                    <label className="toggle border border-base-300 bg-base-200 text-base-content">
                        <input
                            type="checkbox"
                            checked={availability === 'all'}
                            onChange={(event) => onAvailabilityChange(event.target.checked ? 'all' : 'enabled')}
                            aria-label={t('View')}
                        />
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                    </label>
                    <span className="w-24 text-sm">
                        {availability === 'enabled' ? t('Active sensors only') : t('All sensors')}
                    </span>
                </div>

                <div className="flex min-h-12 items-center gap-3 xl:px-5">
                    <label className="text-sm font-semibold" htmlFor="demo-sensor-group-filter-select">
                        {t('Supply type / group')}
                    </label>
                    <select
                        id="demo-sensor-group-filter-select"
                        className="select select-bordered w-auto"
                        value={groupFilter}
                        onChange={(event) => onGroupFilterChange(event.target.value as SensorGroupFilter)}
                    >
                        <optgroup label={t('Group')}>
                            <option value="ALL">{t('All')}</option>
                            <option value="NONE">{t('Without group')}</option>
                            <option value="DTC">DTC</option>
                        </optgroup>
                        <optgroup label={t('Supply type')}>
                            <option value="PV">{t('Photovoltaics')}</option>
                            <option value="BL">{t('Electricity')}</option>
                            <option value="HE">{t('Heat')}</option>
                            <option value="GAS">{t('Gas')}</option>
                            <option value="GRID_FEED">{t('Grid Feed')}</option>
                            <option value="WATER">{t('Water')}</option>
                        </optgroup>
                    </select>
                </div>

                <div className="flex min-h-12 items-center gap-3 xl:px-5">
                    <span className="text-sm font-semibold">{t('Group sensor/device selection by')}</span>
                    <label className="toggle border border-base-300 bg-base-200 text-base-content">
                        <input
                            type="checkbox"
                            checked={groupBy === 'anomaly'}
                            onChange={(event) => onGroupByChange(event.target.checked ? 'anomaly' : 'system')}
                            aria-label={t('Group sensor/device selection by')}
                        />
                        <Network className="h-4 w-4" aria-hidden="true" />
                        <Tags className="h-4 w-4" aria-hidden="true" />
                    </label>
                    <span className="w-24 text-sm">{groupBy === 'system' ? t('System') : t('Anomaly')}</span>
                </div>

                <div className={`flex min-h-12 items-center gap-3 ${extraControl ? 'xl:px-5' : 'xl:pl-5'}`}>
                    <label className="text-sm font-semibold" htmlFor="demo-sensor-device-select">
                        {t('Sensor / device')}
                    </label>
                    <select
                        id="demo-sensor-device-select"
                        className="select select-bordered w-auto"
                        value={selectedDevice}
                        onChange={(event) => onDeviceChange(event.target.value)}
                    >
                        {virtualDeviceGroups.map(({ devices, label }) => (
                            <optgroup key={label} label={label}>
                                {devices.map((device) => (
                                    <option key={device} value={device}>{device}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                {extraControl ? (
                    <div className="flex min-h-12 items-center gap-3 xl:pl-5">
                        {extraControl}
                    </div>
                ) : null}
            </div>
        </section>
    );
}

export default DemoSensorDeviceFilterControls;
